
import './ProjectDetail.css';
import {
  ProjectFullWidth,
  ProjectSideBySide,
  ProjectSideBySideReverse,
  ProjectFooter
} from './ProjectCommon';
import projects from './projects';



function HindsightProjectDetail() {

  // Get project data from projects.js
  const projectData = projects.find(p => p.id === 'hindsight');

  const footerProps = {
    title: "Hindsight",
    description: projectData ? projectData.description : "",
    collaborators: [
      {
        name: "Leslie Liu",
        url: "https://liuleslie.github.io/portfolio/"
      }
    ],
    backgroundImage: "/hindsight/drive_path.webp"
  };

  return (
    <div className="project-detail-root">
      <div className="project-detail-hero">
        <img src="/hindsight/hero.webp" alt="Hindsight Project Hero" className="project-detail-hero-img" />
      </div>
      <div className="project-detail-content">
        <div className="project-detail-intro">
          <div className="project-detail-title">
            <h1>Hindsight</h1>
            <p className="project-credit" style={{ fontStyle: 'italic', color: '#555', marginTop: '-10px', marginBottom: '20px' }}>
              A project by Luke Fiorante and <a href="https://liuleslie.github.io/portfolio/" target="_blank" rel="noopener noreferrer" className="project-link">Leslie Liu</a> at the NASA Jet Propulsion Laboratory.
            </p>
          </div>
          <p>
            <b>Hindsight</b> is an interactive, web-based visualization tool designed for the engineers and operators of <b>NASA's Mars 2020 Perseverance rover mission</b>. We developed this system to accelerate mission-critical analyses by enabling the <b>rapid search and comparison of past rover drive data</b>. The tool's core feature is a novel similarity search engine that allows operators to find analogous past drives based on their characteristics, moving beyond traditional database queries to mirror the expert intuition of the mission's rover operators.
          </p>
        </div>
        <div className="project-detail-process">
          <ProjectFullWidth
            image="/hindsight/one_drive.webp"
            alt="Hindsight drive details view"
            caption="The drive details view showing panels for the orbital map, imagery, telemetry charts, and event records."
          >
            <h2>Motivation</h2>
            <p>
              Every decision for the multi-billion dollar Mars rover is a high-stakes choice that must be rigorously justified. Rover operators are responsible for charting the vehicle's daily path, a process that involves investigating past drive faults and proving that future plans are safe. We discovered that this workflow relies heavily on institutional memory—the collective, often unwritten, knowledge of the entire rover operations team. This reliance on memory is slow, subjective, and creates a significant barrier to training new personnel. This inspired <b>Hindsight</b>, a tool designed to systematize operator knowledge and empower them to make faster, data-driven decisions.
            </p>
          </ProjectFullWidth>

          <ProjectSideBySide image="/hindsight/similarity_search.gif" alt="Hindsight Similarity Search Engine" caption="The similarity search interface.">
            <h2>Similarity-Based Search Engine</h2>
            {/* TODO: once supported, mention: "to a reference drive or a proposed future plan" */}
            <p>
              Hindsight's analytical power comes from its similarity search engine, which allows operators to ask "find me drives that look like this" instead of writing complex parametric queries. The backend, written in Python, uses algorithms like Dynamic Time Warping (DTW) to compare the fundamental "shape" of time-series data (e.g., wheel slip, motor current, terrain type) between drives. This allows the system to identify and rank past drives that are functionally analogous to a reference drive, even if they are of different lengths or occurred in different locations.
            </p>
          </ProjectSideBySide>

          <ProjectSideBySideReverse image="/hindsight/drive_comparison.webp" alt="Hindsight side-by-side comparison interface" caption="Side-by-side comparison interface showing multiple drives.">
            <h2>Side-by-Side Comparison</h2>
            <p>
              To make search results immediately useful, Hindsight presents them in a side-by-side layout. This design allows planners to visually scan for patterns and anomalies across multiple drives at a glance. By aligning key data facets like orbital paths, telemetry charts, and event records in consistent rows, the interface creates a powerful comparative matrix.
            </p>
          </ProjectSideBySideReverse>

          <ProjectSideBySide image="/hindsight/timeline.gif" alt="Hindsight synchronized timeline analysis" caption="Timeline synchronizatio across multiple panels.">
            <h2>Synchronized Timeline Analysis</h2>
            <p>
              When performing a deep-dive on a single drive, a synchronized playhead can be scrubbed along its timeline, simultaneously updating the rover's position on the orbital map, the values in time-series charts, the relevant software event records, and the corresponding rover imagery. This provides a powerful method for correlating disparate data sources to understand exactly what happened at a specific moment in time.
            </p>
          </ProjectSideBySide>

          <ProjectSideBySideReverse image="/hindsight/projection_mapping.webp" alt="Projection mapped Mars terrain" caption="A 3D projection-mapped model of Mars used as a design probe.">
            <h2>Design Methodology</h2>
            <p>
              The initial brief for this project was to build a database of rover drives with support for standard parametric queries. However, we began with a five-week exploratory phase grounded in human-centered design, using <b>design probes</b> to uncover the latent needs and mental models of the rover operators. One key probe was a <b>3D projection-mapped model of Mars's Jezero Crater</b>. We 3D printed a physical terrain map and projected rover paths and data directly onto its surface, allowing us to explore how a spatial understanding of topography helps operators build intuition and collaborate on drive planning.<br /><br />
              <span style={{ fontStyle: 'italic', color: '#555' }}>The projection-mapped model was created with support from ArtCenter Fellow Adam Xu.</span>
            </p>
          </ProjectSideBySideReverse>

          <ProjectSideBySide image="/hindsight/methodology.webp" alt="Hindsight design methodology" caption='Sketches from the "draw your most memorable drive" design probe (top). Usability testing (bottom).'>
            <p>
              Another probe asked operators to "draw their most memorable drive." This exercise showed that an operator's intuition isn't built from filtering isolated data points, but from the holistic character of an entire drive. We realized their goal was to leverage that intuition by finding <b>analogous past situations</b> to inform present decisions. This fundamental need to evaluate similarity became the guiding principle of our design, shifting the project's focus from a simple database to an intuitive, similarity-driven analysis platform.
            </p>
          </ProjectSideBySide>

          <ProjectSideBySideReverse image="/hindsight/tech_stack.webp" alt="Hindsight Technical Stack Diagram" caption="Core technologies powering Hindsight.">
            <h2>Technical Stack and Implementation</h2>
            <p>
              Hindsight is a full-stack web application combining a robust data pipeline with a dynamic interface. The backend is a high-performance Python API built with FastAPI, which serves data and runs the core similarity search logic, supporting both Dynamic Time Warping and k-Nearest Neighbors for evaluating similarity. Data is stored in a PostgreSQL database with the TimescaleDB extension for efficient time-series querying. The frontend is a React application, providing an interactive UI for operators to visually analyze and compare drive data.
            </p>
          </ProjectSideBySideReverse>

        </div>
      </div>
      <ProjectFooter {...footerProps} />
    </div>
  );
}

export default HindsightProjectDetail;
